// Linked List — curated interview problems with multiple worked approaches.
// Same shape as dsaProblems/binarySearch.js: concise steps, a "why & when to use" close per
// approach, and an interactive visualizer clarifying the pointer rewiring.

const linkedListProblems = [
  {
    id: 'reverse-linked-list',
    title: 'Reverse Linked List',
    difficulty: 'Easy',
    leetcode: { number: 206, url: 'https://leetcode.com/problems/reverse-linked-list/' },
    statement: 'Given the head of a singly linked list, reverse the list and return the new head.',
    example: {
      input: 'head = [1, 2, 3, 4, 5]',
      output: '[5, 4, 3, 2, 1]',
      note: 'Every node\'s next pointer now points to what used to be before it.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Collect Into an Array, Rebuild)',
        why:
          "Walk the list once, collecting every value into an array. Then build a brand-new list reading that " +
          "array back to front. Correct, but allocates an entire extra array just to hold values that were " +
          "already sitting in the original nodes.",
        steps: [
          'Walk the original list once, copying each value into an array.',
          'Build a new list by reading the array from its last index to its first.',
          'Return the new list\'s head.',
          'The original nodes are discarded entirely — a full extra array is used instead.',
        ],
        code:
`public ListNode reverseListBruteForce(ListNode head) {
    List<Integer> values = new ArrayList<>();
    for (ListNode curr = head; curr != null; curr = curr.next) values.add(curr.val);

    ListNode newHead = null;
    for (int v : values) {
        newHead = new ListNode(v, newHead);   // prepend — builds the list in reverse order
    }
    return newHead;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — the array, plus a full new set of nodes' },
        whenToUse:
          "Only useful to confirm what \"reversed\" means — the original nodes could have been reused directly " +
          "with no extra memory at all, which is exactly what the optimized approach does.",
      },
      {
        name: 'Approach 2 — Optimized: Iterative Pointer Rewiring',
        why:
          "Reuse the exact same nodes — just point each one's next backward instead of forward, one at a time, " +
          "keeping track of where you came from. No new nodes, no extra array.",
        steps: [
          'Keep prev (starts null) and curr (starts at head).',
          'Save next = curr.next before it gets overwritten.',
          'Set curr.next = prev, then shift both prev and curr one step forward.',
          'When curr becomes null, prev is the new head.',
        ],
        visual: {
          arrays: [{ label: 'nodes', values: [1, 2, 3, 4, 5] }],
          varNames: [],
          steps: [
            { highlights: { nodes: { primary: [0], secondary: [1] } }, note: 'prev=null, curr=node 0 (value 1). Save next=node 1. Set curr.next=prev (null). prev=node0, curr=node1.' },
            { highlights: { nodes: { primary: [1], secondary: [2] } }, note: 'curr=node 1 (value 2). Its next now points back to node 0. prev=node1, curr=node2.' },
            { highlights: { nodes: { primary: [2], secondary: [3] } }, note: 'curr=node 2 (value 3). Its next now points back to node 1. prev=node2, curr=node3.' },
            { highlights: { nodes: { primary: [3], secondary: [4] } }, note: 'curr=node 3 (value 4). Its next now points back to node 2. prev=node3, curr=node4.' },
            { highlights: { nodes: [4] }, note: 'curr=node 4 (value 5), the original tail. Its next now points back to node 3. curr becomes null — loop ends. prev (node 4) is the new head: [5, 4, 3, 2, 1].' },
          ],
        },
        code:
`public ListNode reverseList(ListNode head) {
    ListNode prev = null, curr = head;
    while (curr != null) {
        ListNode next = curr.next;
        curr.next = prev;
        prev = curr;
        curr = next;
    }
    return prev;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        whenToUse:
          "This is the default answer for this problem, and the exact template every other reversal-based " +
          "problem in this pattern (reversing in groups of k, reversing a sub-list) builds directly on top of.",
      },
      {
        name: 'Approach 3 — Recursive',
        why:
          "Reverse everything after the current node first (recursively), then fix up the current node's link " +
          "on the way back out. Elegant to write, but each node adds a call-stack frame — the same trade-off " +
          "covered in the Stack pattern's discussion of the call stack.",
        steps: [
          'Base case: an empty list (or a single node) is already \"reversed\" — return it as-is.',
          'Recursively reverse everything after the head.',
          'Once that returns, fix the link: head.next.next = head (point the next node back at this one).',
          'Set head.next = null (this node is now the new tail) and return the new head from the base case.',
        ],
        code:
`public ListNode reverseListRecursive(ListNode head) {
    if (head == null || head.next == null) return head;

    ListNode newHead = reverseListRecursive(head.next);
    head.next.next = head;
    head.next = null;
    return newHead;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — one call-stack frame per node' },
        interviewNote: "Worth mentioning the trade-off out loud: this reads elegantly but isn't free — n nodes means n stack frames, same risk of a StackOverflowError as any other deep recursion on a long list.",
        whenToUse:
          "Offer this as an alternative once the iterative version is solid, to show comfort with recursion — " +
          "but the iterative version remains the safer default for genuinely long lists.",
      },
    ],
  },
  {
    id: 'merge-two-sorted-lists',
    title: 'Merge Two Sorted Lists',
    difficulty: 'Easy',
    leetcode: { number: 21, url: 'https://leetcode.com/problems/merge-two-sorted-lists/' },
    statement:
      'Given the heads of two sorted linked lists, merge them into a single sorted list by splicing together ' +
      'their nodes, and return the head of the merged list.',
    example: {
      input: 'list1 = [1, 2, 4], list2 = [1, 3, 4]',
      output: '[1, 1, 2, 3, 4]',
      note: 'The smaller of the two current heads is chosen at every step.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Collect, Sort, Rebuild)',
        why:
          "Copy every value from both lists into one array, sort it, then build a brand-new list from the sorted " +
          "array. Correct, but sorting throws away the fact that both inputs were ALREADY sorted.",
        steps: [
          'Walk both lists, copying every value into one array.',
          'Sort that array (even though most of the work was already done).',
          'Build a new list by reading the sorted array in order.',
          'The sort costs O((m+n) log(m+n)) — needlessly, since both inputs were pre-sorted.',
        ],
        code:
`public ListNode mergeTwoListsBruteForce(ListNode list1, ListNode list2) {
    List<Integer> values = new ArrayList<>();
    for (ListNode n = list1; n != null; n = n.next) values.add(n.val);
    for (ListNode n = list2; n != null; n = n.next) values.add(n.val);
    Collections.sort(values);

    ListNode dummy = new ListNode(0), tail = dummy;
    for (int v : values) {
        tail.next = new ListNode(v);
        tail = tail.next;
    }
    return dummy.next;
}`,
        complexity: { time: 'O((m+n) log(m+n))', space: 'O(m+n)' },
        whenToUse:
          "Only useful to confirm the merged order out loud — sorting from scratch ignores the fact both inputs " +
          "arrive pre-sorted, which the optimized approach exploits directly.",
      },
      {
        name: 'Approach 2 — Optimized: Splice Nodes Directly',
        why:
          "Since both lists are already sorted, the smaller of the two current heads is always the next correct " +
          "value overall — just like merging two sorted arrays. Reuse the existing nodes by relinking them onto " +
          "a running result, instead of allocating anything new.",
        steps: [
          'Use a dummy node so the result always has a "previous" to attach to, even for the very first real node.',
          'Compare the current heads of list1 and list2.',
          'Attach whichever is smaller to the result, and advance that list\'s pointer.',
          'Once one list runs out, attach whatever remains of the other list wholesale — it\'s already sorted.',
        ],
        visual: {
          arrays: [{ label: 'list1', values: [1, 2, 4] }, { label: 'list2', values: [1, 3, 4] }],
          varNames: [],
          steps: [
            { highlights: { list1: [0] }, note: 'list1[0]=1 ≤ list2[0]=1 (tie goes to list1) — attach list1\'s node, advance list1.' },
            { highlights: { list2: [0] }, note: 'list1[1]=2 > list2[0]=1 — attach list2\'s node, advance list2.' },
            { highlights: { list1: [1] }, note: 'list1[1]=2 ≤ list2[1]=3 — attach list1\'s node, advance list1.' },
            { highlights: { list2: [1] }, note: 'list1[2]=4 > list2[1]=3 — attach list2\'s node, advance list2.' },
            { highlights: { list1: [2], list2: [2] }, note: 'list1[2]=4 ≤ list2[2]=4 — attach list1\'s node. list1 is now exhausted — attach the rest of list2 (just [4]) wholesale. Final: [1, 1, 2, 3, 4].' },
          ],
        },
        code:
`public ListNode mergeTwoLists(ListNode list1, ListNode list2) {
    ListNode dummy = new ListNode(0), tail = dummy;
    while (list1 != null && list2 != null) {
        if (list1.val <= list2.val) {
            tail.next = list1;
            list1 = list1.next;
        } else {
            tail.next = list2;
            list2 = list2.next;
        }
        tail = tail.next;
    }
    tail.next = (list1 != null) ? list1 : list2;   // attach whatever remains
    return dummy.next;
}`,
        complexity: { time: 'O(m + n)', space: 'O(1) — reuses existing nodes' },
        interviewNote: "Try the \"Merge Two Sorted Lists\" mode in this pattern's interactive playground to run this exact algorithm on any two lists you choose.",
        whenToUse:
          "This dummy-head splicing technique is the standard answer any time two already-sorted structures need " +
          "combining without extra memory — it also generalizes directly to k-way merges (used in Merge K Sorted Lists).",
      },
    ],
  },
  {
    id: 'linked-list-cycle',
    title: 'Linked List Cycle',
    difficulty: 'Easy',
    leetcode: { number: 141, url: 'https://leetcode.com/problems/linked-list-cycle/' },
    statement: 'Given the head of a linked list, determine whether the list has a cycle in it.',
    example: {
      input: 'head = [3, 2, 0, -4], with the tail connecting back to the node at index 1 (value 2)',
      output: 'true',
      note: 'Following .next forever from the head eventually loops back through node 2 endlessly.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (HashSet of Visited Nodes)',
        why:
          "Walk the list, remembering every node object already visited. The instant a node is seen that's " +
          "already in the set, a cycle is confirmed. Correct, but pays for a HashSet entry per node.",
        steps: [
          'Keep a HashSet of node references seen so far.',
          'Walk the list one node at a time.',
          'If the current node is already in the set, a cycle exists.',
          'If curr becomes null before that happens, there\'s no cycle.',
        ],
        code:
`public boolean hasCycleBruteForce(ListNode head) {
    Set<ListNode> visited = new HashSet<>();
    for (ListNode curr = head; curr != null; curr = curr.next) {
        if (!visited.add(curr)) return true;
    }
    return false;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — one HashSet entry per node' },
        whenToUse:
          "A reasonable first answer to state out loud, but the O(1)-space optimized version below is what " +
          "interviewers are almost always looking for on this specific problem.",
      },
      {
        name: 'Approach 2 — Optimized: Floyd\'s Fast & Slow Pointers',
        why:
          "Send two pointers down the list at different speeds. If there's no cycle, the fast one simply reaches " +
          "the end first. If there IS a cycle, the fast pointer eventually laps the slow one from behind, and " +
          "they land on the exact same node — proving the loop, with zero extra memory.",
        steps: [
          'Start both slow and fast at head.',
          'Move slow one step and fast two steps, each iteration.',
          'If fast ever reaches null (or fast.next does), there\'s no cycle.',
          'If slow and fast ever point to the same node, a cycle is confirmed.',
        ],
        visual: {
          arrays: [{ label: 'nodes', values: [3, 2, 0, -4] }],
          varNames: [],
          steps: [
            { highlights: { nodes: { primary: [1], secondary: [3] } }, note: 'slow moves to node 1 (value 2), fast moves to node 3 (value -4) — not equal yet.' },
            { highlights: { nodes: { primary: [2], secondary: [2] } }, note: 'slow moves to node 2 (value 0). fast, moving two steps from node 3, wraps via the cycle (node 3 → node 1 → node 2) and also lands on node 2 — slow and fast meet! Cycle confirmed.' },
          ],
        },
        code:
`public boolean hasCycle(ListNode head) {
    ListNode slow = head, fast = head;
    while (fast != null && fast.next != null) {
        slow = slow.next;
        fast = fast.next.next;
        if (slow == fast) return true;
    }
    return false;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "A natural follow-up question is \"find where the cycle STARTS\" — after slow and fast meet, resetting one pointer to head and advancing both one step at a time makes them meet again exactly at the cycle's starting node (a result worth knowing exists, even if the proof isn't memorized).",
        whenToUse:
          "This is the canonical fast/slow pointer application — whenever a linked list problem asks about a " +
          "cycle, this is the O(1)-space answer, full stop.",
      },
    ],
  },
  {
    id: 'remove-nth-node-from-end-of-list',
    title: 'Remove Nth Node From End of List',
    difficulty: 'Medium',
    leetcode: { number: 19, url: 'https://leetcode.com/problems/remove-nth-node-from-end-of-list/' },
    statement:
      'Given the head of a linked list, remove the nth node from the end of the list, and return the head.',
    example: {
      input: 'head = [1, 2, 3, 4, 5], n = 2',
      output: '[1, 2, 3, 5]',
      note: 'The 2nd node from the end (value 4) is removed.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Two Passes)',
        why:
          "First walk the whole list to find its length. That tells you exactly how many nodes from the FRONT " +
          "to walk before reaching the one to delete. Correct, but the list is walked twice.",
        steps: [
          'First pass: walk the entire list, counting its length L.',
          'The node to remove is at position L − n from the front (using a dummy head to simplify removing the true head).',
          'Second pass: walk that many steps from the dummy, then unlink the following node.',
          'Two full traversals — still O(n), but more work than necessary.',
        ],
        code:
`public ListNode removeNthFromEndBruteForce(ListNode head, int n) {
    int length = 0;
    for (ListNode curr = head; curr != null; curr = curr.next) length++;

    ListNode dummy = new ListNode(0, head);
    ListNode curr = dummy;
    for (int i = 0; i < length - n; i++) curr = curr.next;
    curr.next = curr.next.next;
    return dummy.next;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        whenToUse:
          "A perfectly correct two-pass answer — worth mentioning the one-pass version exists as a natural " +
          "follow-up once this is working.",
      },
      {
        name: 'Approach 2 — Optimized: Two Pointers With a Gap of N',
        why:
          "Advance one pointer n steps ahead of the other FIRST, then move both together. The gap between them " +
          "stays exactly n, so when the front pointer reaches the end, the back pointer is sitting exactly n " +
          "nodes from the end — found in a single pass, no length count needed.",
        steps: [
          'Use a dummy node before head, and a fast pointer starting at the dummy.',
          'Advance fast n + 1 steps ahead (so there\'s exactly a gap of n between fast and a second pointer, slow, both starting at dummy).',
          'Move slow and fast together, one step at a time, until fast reaches the end (null).',
          'slow is now sitting right before the node to remove — unlink it.',
        ],
        visual: {
          arrays: [{ label: 'nodes', values: [1, 2, 3, 4, 5] }],
          varNames: [],
          steps: [
            { highlights: { nodes: [2] }, note: 'fast advances n+1=3 steps ahead from the dummy, landing on node 2 (value 3). slow stays at the dummy, before node 0.' },
            { highlights: { nodes: { primary: [0], secondary: [3] } }, note: 'Move both one step: slow moves to node 0, fast moves to node 3.' },
            { highlights: { nodes: { primary: [1], secondary: [4] } }, note: 'Move both again: slow moves to node 1, fast moves to node 4 — the last node, but fast still isn\'t null, so keep going.' },
            { highlights: { nodes: [2] }, note: 'Move both once more: slow moves to node 2 (value 3), fast falls off the end (null) — stop. slow sits right before the target node.' },
            { note: 'Unlink slow.next — removing node 3 (value 4). Final list: [1, 2, 3, 5].' },
          ],
        },
        code:
`public ListNode removeNthFromEnd(ListNode head, int n) {
    ListNode dummy = new ListNode(0, head);
    ListNode slow = dummy, fast = dummy;

    for (int i = 0; i < n + 1; i++) fast = fast.next;   // open up a gap of n

    while (fast != null) {
        slow = slow.next;
        fast = fast.next;
    }
    slow.next = slow.next.next;   // unlink the target node
    return dummy.next;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        whenToUse:
          "This two-pointer 'gap' technique is the standard one-pass answer whenever a problem needs a position " +
          "measured relative to the END of a structure whose length isn't known upfront.",
      },
    ],
  },
  {
    id: 'reorder-list',
    title: 'Reorder List',
    difficulty: 'Medium',
    leetcode: { number: 143, url: 'https://leetcode.com/problems/reorder-list/' },
    statement:
      'Given the head of a linked list L0 → L1 → … → Ln-1 → Ln, reorder it in place to: ' +
      'L0 → Ln → L1 → Ln-1 → L2 → Ln-2 → … — alternating from the front and back.',
    example: {
      input: 'head = [1, 2, 3, 4, 5]',
      output: '[1, 5, 2, 4, 3]',
      note: 'Front (1), back (5), front (2), back (4), front (3) — the remaining middle element.',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Random-Access Array of Nodes)',
        why:
          "Copy every node reference into an array (arrays DO support random access), then read off the desired " +
          "order — alternating from the front and back of the array — and relink the nodes in that order. " +
          "Correct, but the whole point of this problem is to do it with O(1) extra space, which this doesn't.",
        steps: [
          'Walk the list once, storing every node reference in an array.',
          'Use two pointers on the array — one starting at the front, one at the back — alternating between them.',
          'Relink each chosen node\'s next pointer to whichever node comes next in this new order.',
          'The array of node references costs O(n) extra space.',
        ],
        code:
`public void reorderListBruteForce(ListNode head) {
    List<ListNode> nodes = new ArrayList<>();
    for (ListNode curr = head; curr != null; curr = curr.next) nodes.add(curr);

    int left = 0, right = nodes.size() - 1;
    while (left < right) {
        nodes.get(left).next = nodes.get(right);
        left++;
        if (left == right) break;
        nodes.get(right).next = nodes.get(left);
        right--;
    }
    nodes.get(left).next = null;
}`,
        complexity: { time: 'O(n)', space: 'O(n) — the array of node references' },
        whenToUse:
          "Only useful to confirm the target order out loud — the O(1)-space optimized version below is what " +
          "this problem is specifically testing.",
      },
      {
        name: 'Approach 2 — Optimized: Find Middle, Reverse Second Half, Merge Interleaved',
        why:
          "This problem is a synthesis of three techniques already covered elsewhere in this pattern: fast/slow " +
          "pointers to find the middle (splitting the list into two halves), the prev/curr/next template to " +
          "reverse the second half in place, and then a merge step that interleaves the two halves one node at " +
          "a time — all using the existing nodes, no array needed.",
        steps: [
          'Use fast & slow pointers to find the middle, splitting the list into a first half and a second half.',
          'Reverse the second half in place using the standard prev/curr/next technique.',
          'Merge the two halves by alternating one node from the first half, then one from the (now-reversed) second half.',
          'The first half absorbs the extra node when the total length is odd.',
        ],
        visual: {
          arrays: [{ label: 'firstHalf', values: [1, 2, 3] }, { label: 'secondHalf (reversed)', values: [5, 4] }],
          varNames: [],
          steps: [
            { note: 'Original list: [1, 2, 3, 4, 5]. Fast & slow pointers split it into firstHalf=[1,2,3] and secondHalf=[4,5].' },
            { snapshots: { 'secondHalf (reversed)': [5, 4] }, note: 'Reverse secondHalf in place: [4, 5] becomes [5, 4].' },
            { highlights: { firstHalf: [0], 'secondHalf (reversed)': [0] }, note: 'Interleave: take firstHalf[0]=1, then secondHalf[0]=5.' },
            { highlights: { firstHalf: [1], 'secondHalf (reversed)': [1] }, note: 'Take firstHalf[1]=2, then secondHalf[1]=4.' },
            { highlights: { firstHalf: [2] }, note: 'secondHalf is exhausted — take the one remaining node, firstHalf[2]=3. Final order: [1, 5, 2, 4, 3].' },
          ],
        },
        code:
`public void reorderList(ListNode head) {
    if (head == null || head.next == null) return;

    // 1. Find the middle
    ListNode slow = head, fast = head;
    while (fast.next != null && fast.next.next != null) {
        slow = slow.next;
        fast = fast.next.next;
    }

    // 2. Reverse the second half
    ListNode secondHead = reverseList(slow.next);
    slow.next = null;   // cut the first half loose from the second

    // 3. Merge the two halves, interleaved
    ListNode first = head, second = secondHead;
    while (second != null) {
        ListNode firstNext = first.next;
        ListNode secondNext = second.next;
        first.next = second;
        second.next = firstNext;
        first = firstNext;
        second = secondNext;
    }
}

private ListNode reverseList(ListNode head) {
    ListNode prev = null, curr = head;
    while (curr != null) {
        ListNode next = curr.next;
        curr.next = prev;
        prev = curr;
        curr = next;
    }
    return prev;
}`,
        complexity: { time: 'O(n)', space: 'O(1)' },
        interviewNote: "Naming all three sub-techniques explicitly (find the middle, reverse in place, merge interleaved) as you write this shows the interviewer you recognize it as a composition of earlier patterns, not a brand-new algorithm.",
        whenToUse:
          "This three-step composition — split, reverse, interleave — is worth recognizing as a template of its " +
          "own: any \"reorder from both ends\" problem on a singly linked list tends to reduce to exactly this.",
      },
    ],
  },
  {
    id: 'add-two-numbers',
    title: 'Add Two Numbers',
    difficulty: 'Medium',
    leetcode: { number: 2, url: 'https://leetcode.com/problems/add-two-numbers/' },
    statement:
      'Two non-negative integers are represented as linked lists, where each node holds a single digit, stored ' +
      'in REVERSE order (the ones digit first). Add the two numbers and return the sum as a linked list in the same format.',
    example: {
      input: 'l1 = [2, 4, 3], l2 = [5, 6, 4]',
      output: '[7, 0, 8]',
      note: 'l1 represents 342, l2 represents 465. 342 + 465 = 807, stored in reverse as [7, 0, 8].',
    },
    approaches: [
      {
        name: 'Approach 1 — Brute Force (Convert to Integers, Add, Convert Back)',
        why:
          "Read each list into an actual number (reversing digit order back to normal as you go), add the two " +
          "numbers directly, then split the result back into digit nodes. Intuitive, but risks overflowing a " +
          "primitive int/long for very large inputs, and does more work per digit than necessary.",
        steps: [
          'Walk l1, building an actual integer from its digits (accounting for the reversed digit order).',
          'Do the same for l2.',
          'Add the two integers directly.',
          'Convert the sum back into a linked list of digits, least significant digit first.',
        ],
        code:
`public ListNode addTwoNumbersBruteForce(ListNode l1, ListNode l2) {
    long num1 = toNumber(l1), num2 = toNumber(l2);
    long sum = num1 + num2;

    if (sum == 0) return new ListNode(0);
    ListNode dummy = new ListNode(0), tail = dummy;
    while (sum > 0) {
        tail.next = new ListNode((int) (sum % 10));
        tail = tail.next;
        sum /= 10;
    }
    return dummy.next;
}

private long toNumber(ListNode node) {
    long result = 0, place = 1;
    while (node != null) {
        result += node.val * place;
        place *= 10;
        node = node.next;
    }
    return result;
}`,
        complexity: { time: 'O(m + n)', space: 'O(1) extra (beyond the output)' },
        whenToUse:
          "Works for reasonably small numbers, but risks overflow for very large inputs (hence needing long or " +
          "BigInteger) — the optimized digit-by-digit approach sidesteps that risk entirely by never forming the full number.",
      },
      {
        name: 'Approach 2 — Optimized: Digit-by-Digit Addition With Carry',
        why:
          "This is exactly how addition works on paper: add corresponding digits together along with any carry " +
          "from the previous column, write down the ones digit, and carry the tens digit forward. Since both " +
          "lists already store digits least-significant-first, no conversion or reversal is needed at all.",
        steps: [
          'Walk both lists simultaneously, one digit position at a time.',
          'At each position, sum the two digits (treating a missing digit, once a list runs out, as 0) plus any carry from before.',
          'Append sum % 10 as the new digit, and carry sum / 10 forward.',
          'Keep going as long as either list still has digits, OR there\'s a leftover carry.',
        ],
        visual: {
          arrays: [{ label: 'l1', values: [2, 4, 3] }, { label: 'l2', values: [5, 6, 4] }],
          varNames: ['carry'],
          steps: [
            { highlights: { l1: [0], l2: [0] }, vars: { carry: 0 }, note: '2 + 5 + carry(0) = 7. Digit: 7. New carry: 0.' },
            { highlights: { l1: [1], l2: [1] }, vars: { carry: 1 }, note: '4 + 6 + carry(0) = 10. Digit: 0. New carry: 1.' },
            { highlights: { l1: [2], l2: [2] }, vars: { carry: 0 }, note: '3 + 4 + carry(1) = 8. Digit: 8. New carry: 0. Both lists exhausted and no carry left — done. Result: [7, 0, 8].' },
          ],
        },
        code:
`public ListNode addTwoNumbers(ListNode l1, ListNode l2) {
    ListNode dummy = new ListNode(0), tail = dummy;
    int carry = 0;

    while (l1 != null || l2 != null || carry != 0) {
        int digit1 = (l1 != null) ? l1.val : 0;
        int digit2 = (l2 != null) ? l2.val : 0;
        int sum = digit1 + digit2 + carry;

        carry = sum / 10;
        tail.next = new ListNode(sum % 10);
        tail = tail.next;

        if (l1 != null) l1 = l1.next;
        if (l2 != null) l2 = l2.next;
    }
    return dummy.next;
}`,
        complexity: { time: 'O(max(m, n))', space: 'O(1) extra (beyond the output)' },
        whenToUse:
          "This is the default answer — reach for it any time numbers are represented digit-by-digit as a list " +
          "or array, since it never risks overflowing a primitive type the way converting to a full integer would.",
      },
    ],
  },
];

export default linkedListProblems;
